"use client"

import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { getDay, getShortDay } from "@/lib/ethiopian-date-conversion"
import { toEthiopian } from "ethiopian-calendar-new"
import { useBookingStore } from "@/store/booking-store"

function toYmd(d: Date) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

export default function WeekStrip({
  branchId,
  serviceId,
  initialDate,
  today,
  calendarType,
}: {
  branchId: string
  serviceId: string
  initialDate: string
  today: Date
  calendarType: 'GC' | 'EC'
}) {
  const setBookingState = useBookingStore((state) => state.setBookingState)
  const [selected, setSelected] = useState<Date>(() => new Date(initialDate || Date.now()))
  const {t} = useTranslation();

  // Sync when the incoming initialDate (from store) changes
  useEffect(() => {
    const nextSelected = new Date(initialDate || Date.now())
    setSelected(nextSelected)
  }, [initialDate])

  useEffect(() => {
    // normalize past selection to today if needed
    const t0 = new Date(toYmd(today))
    if (selected < t0) setSelected(t0)
  }, [today, selected])

  const days = useMemo(() => {
    const arr: Date[] = []
    let d = new Date(today)
    while (arr.length < 6) {
      if (d.getDay() !== 0) {
        arr.push(new Date(d))
      }
      d.setDate(d.getDate() + 1)
    }
    return arr
  }, [today])

  function startOfWeekMonday(d: Date) {
    const day = d.getDay() || 7
    const res = new Date(d)
    if (day !== 1) res.setDate(d.getDate() - (day - 1))
    return res
  }

  function selectDate(d: Date) {
    const ymd = toYmd(d)
    setBookingState({ selectedDate: ymd, selectedSlot: null })
    setSelected(d)
  }

  return (
    <div>
      <div className="grid grid-cols-6 gap-2 md:gap-3 mt-2">
        {days.map((d, index) => {
          const {year, month, day} = toEthiopian(d.getFullYear(), d.getMonth() + 1, d.getDate());
          
          const label = calendarType === 'GC' ? d.toLocaleDateString(undefined, { weekday: "short" }) : t(getShortDay(d.getDay()));
          const date = calendarType === 'GC' ? d.getDate() : day;
          const isPast = new Date(toYmd(d)) < new Date(toYmd(today))
          const isSelected = toYmd(d) === toYmd(selected)
          
          return (
            <button
              key={d.toDateString()}
              disabled={isPast}
              onClick={() => selectDate(d)}
              style={{ animationDelay: `${index * 50}ms` }}
              className={`group flex justify-center flex-col items-center rounded-2xl border text-center py-3 md:py-4 shadow-sm transition-all duration-300 animate-fade-in-up opacity-0 fill-mode-forwards ${
                isSelected 
                  ? "border-pink-500 bg-pink-600 shadow-lg shadow-pink-200 scale-105 z-10" 
                  : "border-gray-200 bg-white hover:border-pink-200 hover:shadow-md hover:-translate-y-1" 
              } ${isPast ? "opacity-40 cursor-not-allowed hover:transform-none hover:shadow-none hover:border-gray-100" : ""}`}
            >
              <div className={`text-md md:text-lg font-medium mb-1.5 transition-colors ${isSelected ? "text-pink-100" : "text-gray-500 group-hover:text-pink-500"}`}>
                {t(label)}
              </div>
              <div className={`text-xl md:text-2xl font-bold w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full transition-all ${
                isSelected 
                  ? "text-pink-600 bg-white shadow-sm" 
                  : "text-gray-900 group-hover:bg-pink-50 group-hover:text-pink-600"
              }`}>
                {date}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}


